import { formatDistanceToNow } from 'date-fns'
import { History, Save, Copy, Trash2, Download } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { useNodeHistoryStore, NodeHistoryItem } from '@/stores/nodeHistoryStore'

interface NodeHistoryDialogProps {
  nodeId: string
  projectId: string
  currentNodeData: any
  currentNodeType: string
  onRestoreState: (historyItem: NodeHistoryItem & { nodeData: any }) => void
  onDuplicateNode: (historyItem: NodeHistoryItem & { nodeData: any }) => void
  // Optional external state control
  externalOpen?: boolean
  onExternalOpenChange?: (open: boolean) => void
  showTrigger?: boolean // <-- add this line
  className?: string
}

export const NodeHistoryDialog: React.FC<NodeHistoryDialogProps> = ({
  nodeId,
  projectId,
  currentNodeData,
  currentNodeType,
  onRestoreState,
  onDuplicateNode,
  externalOpen,
  onExternalOpenChange,
  showTrigger = true, // <-- add default
  className = '',
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [history, setHistory] = useState<NodeHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(
    null
  )

  // Use external state if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen
  const setIsOpen = onExternalOpenChange || setInternalOpen

  // Initialize the store
  const nodeHistoryStore = useNodeHistoryStore()

  useEffect(() => {
    if (isOpen) {
      fetchHistory()
    }
  }, [isOpen, nodeId, projectId])

  const fetchHistory = async () => {
    try {
      setLoading(true)
      const historyData = await nodeHistoryStore.getNodeHistory(
        projectId,
        nodeId
      )
      setHistory(historyData || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching node history:', error)
      setLoading(false)
    }
  }

  const handleSaveState = async () => {
    if (!saveName.trim()) return

    try {
      await nodeHistoryStore.saveNodeState({
        projectId,
        nodeId,
        saveName: saveName.trim(),
        description: saveDescription.trim() || undefined,
        nodeData: currentNodeData,
        nodeType: currentNodeType,
        saveType: 'manual',
      })

      setSaveDialogOpen(false)
      setSaveName('')
      setSaveDescription('')
      fetchHistory()
    } catch (error) {
      console.error('Error saving node state:', error)
    }
  }

  const handleRestoreState = async (historyId: string) => {
    try {
      const response = await nodeHistoryStore.getNodeState(historyId)
      onRestoreState(response)
      setIsOpen(false)
    } catch (error) {
      console.error('Error restoring node state:', error)
    }
  }

  const handleDuplicateNode = async (historyId: string) => {
    try {
      const response = await nodeHistoryStore.getNodeState(historyId)
      onDuplicateNode(response)
      setIsOpen(false)
    } catch (error) {
      console.error('Error duplicating node:', error)
    }
  }

  const handleDeleteState = async () => {
    if (!selectedHistoryId) return

    try {
      await nodeHistoryStore.deleteNodeState(selectedHistoryId)
      setDeleteDialogOpen(false)
      setSelectedHistoryId(null)
      fetchHistory()
    } catch (error) {
      console.error('Error deleting node state:', error)
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        {showTrigger && (
          <DialogTrigger asChild>
            <div
              className={`
                flex items-center gap-1 
                px-2 py-1 
                rounded-xs 
                text-xs font-medium
                transition-all duration-200
                cursor-pointer hover:scale-105
                bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200
                ${className}
              `}
              title={`View stored history`}
            >    
              <span>History</span>
            </div>
          </DialogTrigger>
        )}
        <DialogContent
          className="!w-[90vw] !max-w-[90vw] max-h-[80vh] overflow-y-auto"
          style={{ width: '90vw', maxWidth: '90vw' }}
        >
          <DialogHeader>
            <DialogTitle>Node History</DialogTitle>
            <DialogDescription>
              View and manage saved states for this node. You can restore
              previous states or duplicate them as new nodes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-start">
              <Button onClick={() => setSaveDialogOpen(true)} size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save Current State
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-4">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No saved states found for this node.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Saved</TableHead>
                    <TableHead className="text-right">Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.saveName}
                      </TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell className="text-right">
                        {formatDistanceToNow(new Date(item.savedAt), {
                          addSuffix: true,
                        })}
                        <div className="text-xs text-muted-foreground">
                          {item.saveType === 'auto'
                            ? 'Auto-saved'
                            : item.saveType === 'ai_processing'
                              ? 'AI Process save'
                              : 'Manual save'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.nodeType}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex space-x-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreState(item.id)}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Restore
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDuplicateNode(item.id)}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Duplicate
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedHistoryId(item.id)
                              setDeleteDialogOpen(true)
                            }}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save State Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Node State</DialogTitle>
            <DialogDescription>
              Save the current state of this node for future reference or
              restoration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="saveName">Save Name</Label>
              <Input
                id="saveName"
                placeholder="Enter a name for this save state"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="saveDescription">Description (Optional)</Label>
              <Textarea
                id="saveDescription"
                placeholder="Describe what makes this state special"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                rows={6}
                className="!min-h-32"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveState} disabled={!saveName.trim()}>
              Save State
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved State</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this saved state? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteState}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
